import {
	Card,
	Typography,
	Row,
	Col,
	Space,
	Button,
	Tag,
	Descriptions,
	Divider,
	notification,
	Spin,
	Statistic,
	Modal,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	PiGlobeDuotone,
	PiArrowLeftDuotone,
	PiPencilDuotone,
	PiTrashDuotone,
	PiStarDuotone,
	PiCrownDuotone,
	PiShieldCheckDuotone,
	PiLockDuotone,
	PiGearDuotone,
	PiTrendUpDuotone,
} from "react-icons/pi";
import {
	requestGetDomainExtensionById,
	requestDeleteDomainExtension,
} from "../../services/admin.service";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;

const CATEGORY_COLORS = {
	GENERIC: "blue",
	COUNTRY: "green",
	SPONSORED: "purple",
	NEW_GENERIC: "orange",
	REGIONAL: "cyan",
	OTHER: "default",
};

const STATUS_COLORS = {
	ACTIVE: "success",
	INACTIVE: "default",
	COMING_SOON: "processing",
	DEPRECATED: "warning",
};

function ViewDomainExtensionPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(false);
	const [domainExtension, setDomainExtension] = useState(null);

	useEffect(() => {
		if (id) {
			fetchDomainExtension();
		}
	}, [id]);

	const fetchDomainExtension = async () => {
		setLoading(true);
		try {
			const response = await requestGetDomainExtensionById(id);
			setDomainExtension(response.data.data);
		} catch (error) {
			console.error("Error fetching domain extension:", error);
			notification.error({
				message: "Error",
				description: "Failed to fetch domain extension details",
			});
			navigate("/domain-extensions");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = () => {
		Modal.confirm({
			title: "Delete Domain Extension",
			content: `Are you sure you want to delete the "${domainExtension?.name}" domain extension?`,
			okText: "Yes, Delete",
			cancelText: "Cancel",
			okType: "danger",
			onOk: async () => {
				try {
					await requestDeleteDomainExtension(id);
					notification.success({
						message: "Success",
						description: "Domain extension deleted successfully",
					});
					navigate("/domain-extensions");
				} catch (error) {
					console.error("Error deleting domain extension:", error);
					notification.error({
						message: "Error",
						description: error.response?.data?.message || "Failed to delete domain extension",
					});
				}
			},
		});
	};

	if (loading) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", padding: "50px 0" }}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	if (!domainExtension) {
		return null;
	}

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button
						icon={<PiArrowLeftDuotone />}
						onClick={() => navigate("/domain-extensions")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Domain Extension Details
					</Title>
					<Tag color={CATEGORY_COLORS[domainExtension.category]} style={{ borderRadius: "12px" }}>
						{domainExtension.category?.replace(/_/g, " ")}
					</Tag>
					<Tag color={STATUS_COLORS[domainExtension.status]} style={{ borderRadius: "12px" }}>
						{domainExtension.status?.replace(/_/g, " ")}
					</Tag>
				</Space>
				<div style={{ marginTop: 16 }}>
					<Space>
						<Button
							type="primary"
							icon={<PiPencilDuotone />}
							onClick={() => navigate(`/domain-extensions/${id}/edit`)}>
							Edit Extension
						</Button>
						<Button danger icon={<PiTrashDuotone />} onClick={handleDelete}>
							Delete Extension
						</Button>
					</Space>
				</div>
			</div>

			<div className="page-content-wrapper">
				{/* Quick Stats */}
				<Row gutter={16} style={{ marginBottom: 24 }}>
					<Col xs={12} sm={6}>
						<Card size="small">
							<Statistic
								title="Popularity Rank"
								value={domainExtension.popularity?.rank || 999}
								prefix="#"
								valueStyle={{ color: "#1890ff" }}
							/>
						</Card>
					</Col>
					<Col xs={12} sm={6}>
						<Card size="small">
							<Statistic
								title="Orders"
								value={domainExtension.popularity?.orderCount || 0}
								prefix={<PiTrendUpDuotone />}
								valueStyle={{ color: "#52c41a" }}
							/>
						</Card>
					</Col>
					<Col xs={12} sm={6}>
						<Card size="small">
							<Statistic
								title="Registration"
								value={formatCurrency(domainExtension.price?.registration?.amount)}
								valueStyle={{ color: "#faad14" }}
							/>
						</Card>
					</Col>
					<Col xs={12} sm={6}>
						<Card size="small">
							<Statistic
								title="Renewal"
								value={formatCurrency(domainExtension.price?.renewal?.amount)}
								valueStyle={{ color: "#faad14" }}
							/>
						</Card>
					</Col>
				</Row>

				<Row gutter={24}>
					<Col xs={24} lg={16}>
						{/* Basic Information */}
						<Card title="Basic Information" style={{ marginBottom: 24 }}>
							<Descriptions column={1} bordered>
								<Descriptions.Item label="Extension Code">
									<Text code>{domainExtension.code}</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Display Name">
									<Text strong style={{ fontSize: "16px" }}>
										{domainExtension.name}
									</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Description">
									{domainExtension.description || "No description provided"}
								</Descriptions.Item>
								<Descriptions.Item label="Category">
									<Tag color={CATEGORY_COLORS[domainExtension.category]}>
										{domainExtension.category?.replace(/_/g, " ")}
									</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Status">
									<Tag color={STATUS_COLORS[domainExtension.status]}>
										{domainExtension.status?.replace(/_/g, " ")}
									</Tag>
								</Descriptions.Item>
							</Descriptions>
						</Card>

						{/* Pricing */}
						<Card title="Pricing" style={{ marginBottom: 24 }}>
							<Row gutter={16}>
								<Col xs={12} md={6}>
									<Card size="small">
										<Statistic
											title="Registration"
											value={formatCurrency(domainExtension.price?.registration?.amount || 0)}
											precision={2}
										/>
									</Card>
								</Col>
								<Col xs={12} md={6}>
									<Card size="small">
										<Statistic
											title="Renewal"
											value={formatCurrency(domainExtension.price?.renewal?.amount || 0)}
											precision={2}
										/>
									</Card>
								</Col>
								<Col xs={12} md={6}>
									<Card size="small">
										<Statistic
											title="Transfer"
											value={formatCurrency(domainExtension.price?.transfer?.amount || 0)}
											precision={2}
										/>
									</Card>
								</Col>
								<Col xs={12} md={6}>
									<Card size="small">
										<Statistic
											title="Redemption"
											value={formatCurrency(domainExtension.price?.redemption?.amount || 0)}
											precision={2}
										/>
									</Card>
								</Col>
							</Row>
						</Card>

						{/* Features */}
						<Card title="Features" style={{ marginBottom: 24 }}>
							<Row gutter={16}>
								<Col xs={24} md={8}>
									<div style={{ textAlign: "center", padding: "16px" }}>
										<PiShieldCheckDuotone
											size={32}
											style={{
												color: domainExtension.features?.autoRenewal ? "#52c41a" : "#d9d9d9",
											}}
										/>
										<div style={{ marginTop: 8 }}>
											<Text strong>Auto Renewal</Text>
											<br />
											<Text type={domainExtension.features?.autoRenewal ? "success" : "secondary"}>
												{domainExtension.features?.autoRenewal ? "Enabled" : "Disabled"}
											</Text>
										</div>
									</div>
								</Col>
								<Col xs={24} md={8}>
									<div style={{ textAlign: "center", padding: "16px" }}>
										<PiLockDuotone
											size={32}
											style={{
												color: domainExtension.features?.transferLock ? "#52c41a" : "#d9d9d9",
											}}
										/>
										<div style={{ marginTop: 8 }}>
											<Text strong>Transfer Lock</Text>
											<br />
											<Text type={domainExtension.features?.transferLock ? "success" : "secondary"}>
												{domainExtension.features?.transferLock ? "Enabled" : "Disabled"}
											</Text>
										</div>
									</div>
								</Col>
								<Col xs={24} md={8}>
									<div style={{ textAlign: "center", padding: "16px" }}>
										<PiGearDuotone
											size={32}
											style={{
												color: domainExtension.features?.dnsManagement ? "#52c41a" : "#d9d9d9",
											}}
										/>
										<div style={{ marginTop: 8 }}>
											<Text strong>DNS Management</Text>
											<br />
											<Text
												type={domainExtension.features?.dnsManagement ? "success" : "secondary"}>
												{domainExtension.features?.dnsManagement ? "Enabled" : "Disabled"}
											</Text>
										</div>
									</div>
								</Col>
							</Row>
						</Card>

						{/* Restrictions */}
						<Card title="Restrictions" style={{ marginBottom: 24 }}>
							<Descriptions column={2} bordered>
								<Descriptions.Item label="Min Registration Period">
									{domainExtension.restrictions?.minRegistrationPeriod || 1} year(s)
								</Descriptions.Item>
								<Descriptions.Item label="Max Registration Period">
									{domainExtension.restrictions?.maxRegistrationPeriod || 10} year(s)
								</Descriptions.Item>
								<Descriptions.Item label="Min Renewal Period">
									{domainExtension.restrictions?.minRenewalPeriod || 1} year(s)
								</Descriptions.Item>
								<Descriptions.Item label="Max Renewal Period">
									{domainExtension.restrictions?.maxRenewalPeriod || 10} year(s)
								</Descriptions.Item>
								<Descriptions.Item label="Requires Documentation">
									<Tag
										color={
											domainExtension.restrictions?.requiresDocumentation ? "warning" : "success"
										}>
										{domainExtension.restrictions?.requiresDocumentation ? "Yes" : "No"}
									</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Allowed Registrant Types">
									<Space wrap>
										{domainExtension.restrictions?.allowedRegistrantTypes?.map((type) => (
											<Tag key={type}>{type?.replace(/_/g, " ")}</Tag>
										))}
									</Space>
								</Descriptions.Item>
							</Descriptions>
							{domainExtension.restrictions?.restrictedCountries?.length > 0 && (
								<div style={{ marginTop: 16 }}>
									<Text strong>Restricted Countries: </Text>
									<Space wrap>
										{domainExtension.restrictions.restrictedCountries.map((country) => (
											<Tag key={country} color="red">
												{country}
											</Tag>
										))}
									</Space>
								</div>
							)}
						</Card>
					</Col>

					<Col xs={24} lg={8}>
						{/* Popularity & Marketing */}
						<Card title="Popularity & Marketing" style={{ marginBottom: 24 }}>
							<div style={{ marginBottom: 16 }}>
								<Text strong>Popularity Rank: </Text>
								<Text style={{ fontSize: "16px", color: "#1890ff" }}>
									#{domainExtension.popularity?.rank || 999}
								</Text>
							</div>
							<div style={{ marginBottom: 16 }}>
								<Text strong>Order Count: </Text>
								<Text style={{ fontSize: "16px", color: "#52c41a" }}>
									{domainExtension.popularity?.orderCount || 0}
								</Text>
							</div>
							<Space direction="vertical" size={8}>
								{domainExtension.popularity?.isPopular && (
									<Tag color="gold" icon={<PiStarDuotone />}>
										Popular
									</Tag>
								)}
								{domainExtension.popularity?.isFeatured && (
									<Tag color="purple" icon={<PiCrownDuotone />}>
										Featured
									</Tag>
								)}
							</Space>
						</Card>

						{/* Registrar Information */}
						<Card title="Registrar Information" style={{ marginBottom: 24 }}>
							<Descriptions column={1} size="small">
								<Descriptions.Item label="Name">
									{domainExtension.registrar?.name || "Not specified"}
								</Descriptions.Item>
								<Descriptions.Item label="Wholesale">
									<Tag color={domainExtension.registrar?.wholesale ? "success" : "default"}>
										{domainExtension.registrar?.wholesale ? "Yes" : "No"}
									</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="API Supported">
									<Tag color={domainExtension.registrar?.apiSupported ? "success" : "default"}>
										{domainExtension.registrar?.apiSupported ? "Yes" : "No"}
									</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Manual Registration">
									<Tag
										color={domainExtension.registrar?.manualRegistration ? "success" : "default"}>
										{domainExtension.registrar?.manualRegistration ? "Yes" : "No"}
									</Tag>
								</Descriptions.Item>
							</Descriptions>
						</Card>

						{/* SEO Information */}
						{(domainExtension.seo?.title ||
							domainExtension.seo?.description ||
							domainExtension.seo?.keywords?.length > 0) && (
							<Card title="SEO Information" style={{ marginBottom: 24 }}>
								<Descriptions column={1} size="small">
									{domainExtension.seo?.title && (
										<Descriptions.Item label="Title">{domainExtension.seo.title}</Descriptions.Item>
									)}
									{domainExtension.seo?.description && (
										<Descriptions.Item label="Description">
											{domainExtension.seo.description}
										</Descriptions.Item>
									)}
									{domainExtension.seo?.keywords?.length > 0 && (
										<Descriptions.Item label="Keywords">
											<Space wrap>
												{domainExtension.seo.keywords.map((keyword) => (
													<Tag key={keyword}>{keyword}</Tag>
												))}
											</Space>
										</Descriptions.Item>
									)}
								</Descriptions>
							</Card>
						)}

						{/* Metadata */}
						{domainExtension.metadata && Object.keys(domainExtension.metadata).length > 0 && (
							<Card title="Metadata" style={{ marginBottom: 24 }}>
								<pre
									style={{
										backgroundColor: "#f5f5f5",
										padding: "12px",
										borderRadius: "4px",
										fontSize: "12px",
										overflow: "auto",
									}}>
									{JSON.stringify(domainExtension.metadata, null, 2)}
								</pre>
							</Card>
						)}

						{/* Timestamps */}
						<Card title="System Information" size="small">
							<Descriptions column={1} size="small">
								<Descriptions.Item label="Created">
									{new Date(domainExtension.createdAt).toLocaleDateString()}
								</Descriptions.Item>
								<Descriptions.Item label="Last Updated">
									{new Date(domainExtension.updatedAt).toLocaleDateString()}
								</Descriptions.Item>
								<Descriptions.Item label="ID">
									<Text code>{domainExtension._id}</Text>
								</Descriptions.Item>
							</Descriptions>
						</Card>
					</Col>
				</Row>
			</div>
		</div>
	);
}

export default ViewDomainExtensionPage;
